/**
 * Local draft persistence.
 *
 * "Never lose work on a failed save" cannot be satisfied by retrying alone: a retry loop dies
 * with the tab. So every mutation also lands in localStorage under the image id, and the
 * workspace offers to restore it when the image is opened again.
 *
 * It is offered, never applied silently. A stale draft resurrected without the annotator
 * noticing would overwrite whatever they (or a colleague) saved in the meantime, and the bulk
 * upsert's submitted list is authoritative — so a silent restore is a silent delete.
 */

import type { OrientedRect, Rect } from '../coords';
import type { DraftBox } from '../draft';
import type { LayoutType, ReadingDirection } from '../api/types';

const PREFIX = 'hismaic.draft.';

/**
 * The draft format version.
 *
 * * **1** — boxes with an axis-aligned `bbox`.
 * * **2** — boxes with an oriented one (`angle`, `kind`).
 * * **3** — boxes with a `chirality` beside the deprecated `mirrored` boolean.
 *
 * A bump used to mean "discard". That was tolerable while the only reader was a developer's
 * own browser; it is not now, because the people this runs for work against the deployed
 * service and would lose an in-flight image to a bundle update they never asked for. So a
 * version this file knows how to read forward is migrated instead — see `upgradeDraft`.
 */
const VERSION = 3;

export interface StoredDraft {
  version: number;
  imageId: number;
  annotatorId: string;
  savedAt: string;
  boxes: DraftBox[];
  absent: number[];
  readingDirection: ReadingDirection | null;
  readingDirectionAngle: number | null;
  layoutType: LayoutType | null;
}

function key(imageId: number): string {
  return `${PREFIX}${imageId}`;
}

/**
 * How old a draft can be before it is offered with a warning rather than as work in progress.
 *
 * A draft older than this is more likely to be a forgotten tab from a fortnight ago, and
 * restoring it would resurrect boxes drawn against an image whose annotations have since moved
 * on. It is **not** deleted for that: see `EditorSession#offerDraft`. The only thing age buys
 * is a label.
 */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Persist a draft. Returns whether it reached storage.
 *
 * The return value is load-bearing: the session refuses to clear boxes it could not persist,
 * because a quota failure plus a reset is the one path where work goes from "unsaved" to
 * "gone". A failure is still not worth an exception — the in-memory state and the retry loop
 * are untouched.
 */
export function saveDraft(draft: Omit<StoredDraft, 'version' | 'savedAt'>): boolean {
  if (typeof localStorage === 'undefined') return false;
  const payload: StoredDraft = { ...draft, version: VERSION, savedAt: new Date().toISOString() };
  try {
    localStorage.setItem(key(draft.imageId), JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Bring an older draft forward, or return `null` when it cannot be read.
 *
 * v1 boxes predate the rotatable canvas, so they are upright and they are `'aabb'` — which is
 * the honest label, not merely a convenient default: they were drawn when there was no other
 * option, and that is exactly what `'aabb'` records.
 *
 * v1 and v2 boxes predate `chirality`, so they arrive as `null`: the bundle that wrote them
 * could not ask the question, so nobody looked. Never `'reference'` — a boolean `mirrored` is
 * not a chirality and inventing one from it is the derivation the column exists to end.
 *
 * A version this function does not recognise is still refused. Guessing at an unknown shape is
 * how a draft comes back with geometry that means something different from what it says.
 */
export function upgradeDraft(parsed: StoredDraft): StoredDraft | null {
  if (parsed.version === VERSION) return parsed;
  if (parsed.version !== 1 && parsed.version !== 2) return null;
  // A v1 box's `bbox` has no `angle` or `kind` at all, whatever the declared type of the
  // parsed object says — it was written by a bundle that had never heard of them. Spelling
  // the old shape out is what stops a spread from being relied on to leave two fields alone.
  const upright = parsed.version === 1;
  const boxes = parsed.boxes as unknown as (Omit<DraftBox, 'bbox' | 'chirality'> & {
    bbox: Rect & Partial<Pick<OrientedRect, 'angle' | 'kind'>>;
  })[];
  return {
    ...parsed,
    version: VERSION,
    boxes: boxes.map((box) => ({
      ...box,
      chirality: null,
      bbox: upright
        ? { x: box.bbox.x, y: box.bbox.y, w: box.bbox.w, h: box.bbox.h, angle: 0, kind: 'aabb' }
        : {
            x: box.bbox.x,
            y: box.bbox.y,
            w: box.bbox.w,
            h: box.bbox.h,
            angle: box.bbox.angle ?? 0,
            kind: box.bbox.kind ?? 'aabb',
          },
    })),
  };
}

export function loadDraft(imageId: number): StoredDraft | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key(imageId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (parsed.imageId !== imageId) return null;
    if (!Array.isArray(parsed.boxes)) return null;
    return upgradeDraft(parsed);
  } catch {
    return null;
  }
}

export function clearDraft(imageId: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key(imageId));
  } catch {
    /* nothing useful to do */
  }
}

/**
 * How old the draft is, or `null` when the timestamp cannot be read.
 *
 * `null` and not `Infinity`. It used to be `Infinity`, which made every unreadable timestamp
 * "older than a fortnight" and — while expiry still deleted — meant one corrupt character in
 * `savedAt` silently destroyed a live draft. An unknown age is unknown, not ancient.
 */
export function draftAgeMs(draft: StoredDraft, now: number = Date.now()): number | null {
  const saved = Date.parse(draft.savedAt);
  return Number.isFinite(saved) ? now - saved : null;
}

/**
 * Is this draft old enough, or unreadable enough, to need a warning before it is restored?
 *
 * Named `stale` rather than `expired` because nothing expires: the caller offers it either way.
 * An unreadable timestamp counts as stale — the annotator should look at it — but that is a
 * label on an offer, not a licence to delete.
 */
export function isDraftStale(draft: StoredDraft, now: number = Date.now()): boolean {
  const age = draftAgeMs(draft, now);
  return age === null || age > DRAFT_MAX_AGE_MS;
}

/**
 * Whose draft this is, relative to the annotator now at the keyboard.
 *
 * `annotatorId` was written and never read, so on a shared browser annotator B was offered A's
 * unsaved boxes and, on restore, would have saved them under B's `annotator_id`. Provenance is
 * non-nullable for a reason; smearing it across two people is not something an export can
 * later unpick. The draft is still offered — it is real work — but it is labelled.
 */
export function draftOwnership(
  draft: StoredDraft,
  currentAnnotatorId: string,
): 'mine' | 'other' | 'unattributed' {
  if (!draft.annotatorId) return 'unattributed';
  return draft.annotatorId === currentAnnotatorId ? 'mine' : 'other';
}

/** Deep copy of a box list. Used for the undo stack and for draft snapshots. */
export function cloneBoxes(boxes: readonly DraftBox[]): DraftBox[] {
  return boxes.map((box) => ({ ...box, bbox: { ...box.bbox } }));
}
